import Button from 'devextreme-react/button';
import DataGrid, { Column, Toolbar, type DataGridRef } from 'devextreme-react/data-grid';
import LoadIndicator from 'devextreme-react/load-indicator';
import {
  OPS_SITE_GRID_PROPS,
  OpsGridToolbarItems,
  OpsSiteGridChrome,
  showErrorToast,
  showSuccessToast
} from '@jarvis/platform-ui';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import {
  fetchClientLicenseSummary,
  fetchClientLicenseSummarySyncStatus,
  listClientLicenseUsersMissingP2,
  triggerClientLicenseSummarySync,
  type ClientLicenseSummary,
  type ClientLicenseUserMissingP2
} from '../../../api/clientLicenseSummary';
import { listClientPortalTenants } from '../../../api/clientAccountsUsersAdmin';

const GRID_STATE_KEY = 'webopsui:client-license-summary-missing-p2:v1';

function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return value;
  return when.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function SummaryCard({ label, value, tone }: { label: string; value: number | string; tone?: 'warn' | 'ok' }) {
  const valueStyle: CSSProperties =
    tone === 'warn'
      ? { color: 'var(--dx-color-danger, #dc2626)' }
      : tone === 'ok'
        ? { color: 'var(--ops-status-succeeded-fg, #166534)' }
        : {};
  return (
    <div
      style={{
        flex: '1 1 160px',
        minWidth: 140,
        padding: '0.875rem 1rem',
        borderRadius: 8,
        border: '1px solid var(--ops-border, #e5e7eb)',
        background: 'var(--ops-surface, #fff)'
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ops-color-muted, #6b7280)', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, ...valueStyle }}>{value}</div>
    </div>
  );
}

function AccountEnabledPill({ enabled }: { enabled: boolean }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.125rem 0.5rem',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: enabled ? 'var(--ops-status-succeeded-bg, #dcfce7)' : 'var(--ops-status-failed-bg, #fee2e2)',
        color: enabled ? 'var(--ops-status-succeeded-fg, #166534)' : 'var(--ops-status-failed-fg, #b91c1c)'
      }}
    >
      {enabled ? 'Enabled' : 'Disabled'}
    </span>
  );
}

export function ClientLicenseSummaryAdminPage() {
  const navigate = useNavigate();
  const gridRef = useRef<DataGridRef>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [summary, setSummary] = useState<ClientLicenseSummary | null>(null);
  const [rows, setRows] = useState<ClientLicenseUserMissingP2[]>([]);
  const [jobFinishedAt, setJobFinishedAt] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listClientPortalTenants();
        if (cancelled) return;
        if (items.length === 0) {
          setTenantError('No client tenants are configured. Add ClientGraph__Tenants__N__* to WebOps env.');
          setTenantId(null);
        } else {
          setTenantId(items[0].tenantId);
          setTenantError(null);
        }
      } catch (e: unknown) {
        if (!cancelled) setTenantError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(async () => {
    if (!tenantId) {
      setSummary(null);
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const [summaryRes, syncStatus, missing] = await Promise.all([
        fetchClientLicenseSummary(tenantId),
        fetchClientLicenseSummarySyncStatus(tenantId),
        listClientLicenseUsersMissingP2(tenantId, { take: 500 })
      ]);
      setSummary(summaryRes);
      setRows(missing.items);
      setJobFinishedAt(syncStatus.lastSyncedAt);
      setJobStatus(syncStatus.lastRunStatus);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load license summary');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const triggerSync = async () => {
    if (!tenantId) return;
    setSyncing(true);
    try {
      await triggerClientLicenseSummarySync(tenantId);
      showSuccessToast('License summary sync queued.');
      window.setTimeout(() => void load(), 3000);
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to trigger sync');
    } finally {
      setSyncing(false);
    }
  };

  const openInHvgClients = (row: ClientLicenseUserMissingP2) => {
    navigate('/admin/client-accounts/clients', { state: { focusUserId: row.id } });
  };

  if (bootstrapping) {
    return (
      <div className="ops-fill-page ops-fill-page--bare" style={{ padding: '1rem', display: 'flex', gap: 8, alignItems: 'center' }}>
        <LoadIndicator visible height={20} width={20} />
        <span>Loading…</span>
      </div>
    );
  }

  if (tenantError || !tenantId) {
    return (
      <div className="ops-fill-page ops-fill-page--bare" style={{ padding: '1rem' }}>
        <div style={{ color: 'var(--dx-color-danger, #dc2626)', fontSize: 14 }}>
          {tenantError ?? 'No client tenant configured.'}
        </div>
      </div>
    );
  }

  const bannerError =
    summary?.collectionError ??
    (summary && !summary.p2SkuId
      ? `Entra ID P2 (${summary.p2SkuPartNumber ?? 'AAD_PREMIUM_P2'}) was not found. Grant Organization.Read.All on the ClientGraph app, admin-consent, then Run sync.`
      : null);

  return (
    <div className="ops-fill-page ops-fill-page--bare" style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0.75rem 1rem 1rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>License Summary</div>
          <div style={{ fontSize: 13, color: 'var(--ops-color-muted, #6b7280)', marginTop: 4 }}>
            {summary?.tenantDisplayName ?? tenantId} · Entra ID P2 ({summary?.p2SkuPartNumber ?? 'AAD_PREMIUM_P2'})
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--ops-color-muted, #6b7280)' }}>
            Cache synced: {formatWhen(summary?.syncedAt)}
          </span>
          <span style={{ fontSize: 13, color: 'var(--ops-color-muted, #6b7280)' }}>
            Last job: {formatWhen(jobFinishedAt)}
            {jobStatus ? ` (${jobStatus})` : ''}
          </span>
          <Button icon="refresh" stylingMode="outlined" disabled={loading} onClick={() => void load()} />
          <Button
            text="Run sync"
            icon="pulldown"
            type="default"
            stylingMode="contained"
            disabled={syncing || loading}
            onClick={() => void triggerSync()}
          />
        </div>
      </div>

      {bannerError ? (
        <div
          style={{
            padding: '0.75rem 1rem',
            borderRadius: 8,
            background: 'var(--ops-status-failed-bg, #fee2e2)',
            color: 'var(--ops-status-failed-fg, #b91c1c)',
            fontSize: 14
          }}
        >
          {bannerError}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <SummaryCard label="Total P2" value={summary?.prepaidUnits ?? 0} />
        <SummaryCard label="Assigned" value={summary?.consumedUnits ?? 0} />
        <SummaryCard
          label="Available"
          value={summary?.availableUnits ?? 0}
          tone={(summary?.availableUnits ?? 0) === 0 ? 'warn' : 'ok'}
        />
        <SummaryCard
          label="Users missing P2"
          value={summary?.usersMissingP2 ?? 0}
          tone={(summary?.usersMissingP2 ?? 0) > 0 ? 'warn' : undefined}
        />
      </div>

      <div className="ops-fill-page__grid-host" style={{ flex: 1, minHeight: 280 }}>
        <DataGrid
          ref={gridRef}
          dataSource={rows}
          keyExpr="id"
          height="100%"
          width="100%"
          showBorders
          columnAutoWidth
          {...OPS_SITE_GRID_PROPS}
          noDataText="All present directory users have Entra ID P2 assigned."
        >
          <OpsSiteGridChrome
            stateStorageKey={`${GRID_STATE_KEY}:${tenantId}`}
            schemaVersion={1}
            stateColumns={['displayName', 'userPrincipalName', 'accountEnabled']}
            searchPlaceholder="Search users missing P2…"
            pageSize={50}
          />
          <Toolbar>
            <OpsGridToolbarItems
              gridRef={gridRef}
              loading={loading}
              onRefresh={() => void load()}
              refreshHint="Refresh license data"
            />
          </Toolbar>
          <Column dataField="displayName" caption="Display name" />
          <Column dataField="userPrincipalName" caption="UPN" />
          <Column
            dataField="accountEnabled"
            caption="Account"
            cellRender={({ data }) => <AccountEnabledPill enabled={Boolean(data.accountEnabled)} />}
          />
          <Column
            caption=""
            width={56}
            allowSorting={false}
            allowFiltering={false}
            cellRender={({ data }) => (
              <Button icon="user" hint="Open in HVG Clients" stylingMode="text" onClick={() => openInHvgClients(data)} />
            )}
          />
        </DataGrid>
      </div>
    </div>
  );
}
